import type { CascadeSlotConfig, GridCell, WinResult } from '@core/types/cascade-slot.types';

export function getPayoutBracketIndex(count: number, brackets: number[]): number {
  // brackets = [8, 10, 12]
  // count >= 12 -> 2, count >= 10 -> 1, count >= 8 -> 0
  for (let i = brackets.length - 1; i >= 0; i--) {
    if (count >= brackets[i]) return i;
  }
  return -1;
}

export function evaluateWins(grid: GridCell[][], config: CascadeSlotConfig): WinResult[] {
  if (config.winType === 'ways') {
    return evaluateWaysWins(grid, config);
  }
  return evaluateCountWins(grid, config);
}

/**
 * Count-based evaluation (Gates of Olympus style).
 * Counts all matching symbols anywhere on grid; wins if count >= minMatchCount.
 */
function evaluateCountWins(grid: GridCell[][], config: CascadeSlotConfig): WinResult[] {
  const results: WinResult[] = [];
  const { symbols, minMatchCount, countBrackets } = config;

  for (const symbol of symbols) {
    if (symbol.category === 'scatter' || symbol.category === 'bonus' || symbol.category === 'wild') continue;
    if (symbol.paytable.length === 0) continue;

    let count = 0;
    const positions: { row: number; col: number }[] = [];

    for (let i = 0; i < grid.length; i++) {
      for (let j = 0; j < grid[i].length; j++) {
        if (grid[i][j].symbolId === symbol.id) {
          count++;
          positions.push({ row: i, col: j });
        }
      }
    }

    if (count >= minMatchCount) {
      const bracketIndex = getPayoutBracketIndex(count, countBrackets);
      if (bracketIndex >= 0 && bracketIndex < symbol.paytable.length) {
        results.push({
          symbolId: symbol.id,
          count,
          positions,
          payout: symbol.paytable[bracketIndex].payout,
          bracketIndex,
        });
      }
    }
  }

  return results;
}

/**
 * Ways-pay evaluation (Mahjong Ways style).
 * Wins require matching symbols on consecutive reels from left.
 * Payout = paytable value × number of ways.
 * Wild symbols substitute for any regular symbol.
 */
function evaluateWaysWins(grid: GridCell[][], config: CascadeSlotConfig): WinResult[] {
  const results: WinResult[] = [];
  const { symbols, minMatchCount, countBrackets, columns } = config;

  // Collect wild positions per column
  const wildPositionsPerCol: { row: number; col: number }[][] = Array.from({ length: columns }, () => []);
  const wildCountPerCol: number[] = new Array(columns).fill(0);

  for (let i = 0; i < grid.length; i++) {
    for (let j = 0; j < grid[i].length; j++) {
      const cell = grid[i][j];
      if (!cell.symbolId) continue;
      const sym = symbols.find(s => s.id === cell.symbolId);
      if (sym && sym.category === 'wild') {
        wildCountPerCol[j]++;
        wildPositionsPerCol[j].push({ row: i, col: j });
      }
    }
  }

  for (const symbol of symbols) {
    if (symbol.category === 'scatter' || symbol.category === 'bonus' || symbol.category === 'wild') continue;
    if (symbol.paytable.length === 0) continue;

    // Count this symbol's occurrences per column (reel)
    const countPerCol: number[] = new Array(columns).fill(0);
    const positionsPerCol: { row: number; col: number }[][] = Array.from({ length: columns }, () => []);

    for (let i = 0; i < grid.length; i++) {
      for (let j = 0; j < grid[i].length; j++) {
        if (grid[i][j].symbolId === symbol.id) {
          countPerCol[j]++;
          positionsPerCol[j].push({ row: i, col: j });
        }
      }
    }

    // Find consecutive reels from left that have the symbol (or wild)
    let consecutiveReels = 0;
    for (let col = 0; col < columns; col++) {
      const totalOnReel = countPerCol[col] + wildCountPerCol[col];
      if (totalOnReel > 0) {
        consecutiveReels++;
      } else {
        break; // Must be consecutive from reel 0
      }
    }

    if (consecutiveReels < (minMatchCount || 3)) continue;

    // Calculate ways and collect positions for the winning reels
    let ways = 1;
    const allPositions: { row: number; col: number }[] = [];

    for (let col = 0; col < consecutiveReels; col++) {
      const reelTotal = countPerCol[col] + wildCountPerCol[col];
      ways *= reelTotal;
      allPositions.push(...positionsPerCol[col]);
      allPositions.push(...wildPositionsPerCol[col]);
    }

    // Find payout bracket based on number of consecutive reels
    const bracketIndex = getPayoutBracketIndex(consecutiveReels, countBrackets);
    if (bracketIndex >= 0 && bracketIndex < symbol.paytable.length) {
      results.push({
        symbolId: symbol.id,
        count: consecutiveReels,
        positions: allPositions,
        payout: symbol.paytable[bracketIndex].payout * ways,
        bracketIndex,
      });
    }
  }

  return results;
}
